"use client";

import React, { useRef, useState } from "react";
import Image from "next/image";

import { Button } from "@/components/ui/button";

import { useLogin } from "@/hooks/useAuthMutations";
import CustomInput from "@/app/_components/CustomInput";
import Heading from "@/app/_components/Heading";

import ErrorBanner from "@/app/_components/ErrorBanner";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { loginSchema } from "@/schema/auth";
import type { z } from "zod";
import Link from "next/link";

interface EmailLoginProps {
  onSwitchToOTP: () => void;
}

const EmailLogin = ({ onSwitchToOTP }: EmailLoginProps) => {
  const login = useLogin();

  const [isErrorDismissed, setIsErrorDismissed] = useState(false);

  // react-hook-form for client validation
  type FormValues = z.infer<typeof loginSchema>;
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(loginSchema),
    mode: "onSubmit",
    reValidateMode: "onSubmit",
  });

  const formRef = useRef<HTMLFormElement | null>(null);

  const dismissError = () => {
    setIsErrorDismissed(true);
  };

  return (
    <div className="flex max-w-120 flex-col">
      {login.isError && !isErrorDismissed && (
        <ErrorBanner
          message={(login.error as Error)?.message || "Login failed"}
          onDismiss={dismissError}
        />
      )}
      
      <Heading size="3xl" className="text-text-900 mb-10.5">
        Admin Login
      </Heading>

      <form
        ref={formRef}
        className="mb-4 flex flex-col gap-4"
        noValidate
        onSubmit={handleSubmit((values) => {
          setIsErrorDismissed(false);
          login.mutate({ email: values.email, password: values.password });
        })}
      >
        <CustomInput
          label="Enter Email"
          type="email"
          placeholder="example@gmail.com"
          error={errors.email?.message}
          {...register("email")}
          name="email"
        />

        <CustomInput
          label="Enter Password"
          type="password"
          placeholder="Enter your password"
          error={errors.password?.message}
          {...register("password")}
          name="password"
          showPasswordToggle
        />

        <Button type="submit" disabled={login.isPending || isSubmitting}>
          {"Login"}
        </Button>
      </form>
      <Link href={'/forgot-password'}>
      <div className="text-text-900 text-body-semibold/md mb-7">Forgot Password?</div>
      </Link>
      <div className="mb-7 flex w-full items-center">
        <div className="bg-outline-600 h-px flex-grow"></div>
        <span className="text-body-medium/sm text-text-950 mx-3">OR</span>
        <div className="bg-outline-600 h-px flex-grow"></div>
      </div>
      <Button variant={"tertiary"} onClick={onSwitchToOTP}>
        <span className="mr-3">Login with Phone</span>{" "}
        <Image src="/images/login/call.svg" alt="call" height={18} width={18} />
      </Button>
    </div>
  );
};

export default EmailLogin;
